import React, { useState } from "react";
import DashboardShell from "@/components/DashboardShell";
import FixModal from "@/components/FixModal";
import { useProject } from "@/context/ProjectContext";
import api from "@/lib/api";
import {
  ScanText,
  AlertTriangle,
  Wand2,
  FileCode2,
  FileText,
  ScissorsSquare,
  RefreshCw,
  AlignLeft,
  Check,
  Sparkles,
} from "lucide-react";

const TYPE_META = {
  missing_faq_schema: { icon: FileCode2, label: "FAQ schema" },
  no_llms_txt: { icon: FileText, label: "llms.txt" },
  not_answer_first: { icon: AlignLeft, label: "Answer-first" },
  section_too_long: { icon: ScissorsSquare, label: "Too long" },
  stale_content: { icon: RefreshCw, label: "Stale" },
};

const SEV_CLS = {
  high: "bg-rose-50 text-rose-700 border-rose-200",
  medium: "bg-amber-50 text-amber-700 border-amber-200",
  low: "bg-zinc-50 text-zinc-700 border-zinc-200",
};

export default function Optimizer() {
  const { current, projects, selectProject } = useProject();
  const [url, setUrl] = useState("");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [generating, setGenerating] = useState({});
  const [selectedArtefact, setSelectedArtefact] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!url.trim()) return;
    setLoading(true);
    setError(null);
    setReport(null);
    try {
      const r = await api.post("/optimizer/audit", { url: url.trim() });
      // Re-fetch with hydration (findings include action + artefact)
      const r2 = await api.get(`/optimizer/reports/${r.data.report_id}`);
      setReport(r2.data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Audit failed");
    } finally {
      setLoading(false);
    }
  };

  const generateFix = async (finding) => {
    setGenerating((g) => ({ ...g, [finding.finding_id]: true }));
    try {
      const r = await api.post("/artefacts/generate", {
        report_id: report.report_id,
        finding_id: finding.finding_id,
      });
      // Update finding inline
      setReport((prev) => ({
        ...prev,
        findings: prev.findings.map((f) =>
          f.finding_id === finding.finding_id
            ? { ...f, artefact: r.data.artefact, action: r.data.action }
            : f
        ),
      }));
      setSelectedArtefact(r.data.artefact);
    } catch (e) {
      setError(e?.response?.data?.detail || "Fix generation failed");
    } finally {
      setGenerating((g) => ({ ...g, [finding.finding_id]: false }));
    }
  };

  const toggleCompleted = async (finding, nextChecked) => {
    const r = await api.post("/actions/toggle", {
      finding_id: finding.finding_id,
      completed: nextChecked,
    });
    setReport((prev) => ({
      ...prev,
      findings: prev.findings.map((f) =>
        f.finding_id === finding.finding_id ? { ...f, action: r.data } : f
      ),
    }));
  };

  const findings = report?.findings || [];
  const highCount = findings.filter((f) => f.severity === "high").length;
  const completedCount = findings.filter((f) => f.action?.status === "completed").length;

  return (
    <DashboardShell project={current} projects={projects} onProjectChange={selectProject}>
      <div className="mb-8">
        <div className="label-eyebrow">Action</div>
        <h1 className="display mt-2 text-4xl font-black tracking-tighter">
          Content Optimizer
        </h1>
        <p className="mt-2 text-zinc-600 text-sm max-w-xl">
          Paste a URL. Every finding below comes with a one-click "Fix this"
          button that generates a copy-paste ready artefact.
        </p>
      </div>

      <form
        onSubmit={submit}
        className="card-flat p-5 flex items-end gap-3"
        data-testid="optimizer-form"
      >
        <div className="flex-1">
          <label className="label-eyebrow">Page URL</label>
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://yourdomain.com/blog/post"
            required
            data-testid="optimizer-url"
            className="mt-1 w-full bg-white border border-zinc-200 rounded-[4px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black/10"
          />
        </div>
        <button
          type="submit"
          disabled={loading || !url.trim()}
          data-testid="optimizer-run"
          className="inline-flex items-center gap-1.5 bg-black text-white text-sm px-4 py-2.5 rounded-[4px] hover:bg-zinc-800 transition-colors disabled:opacity-50"
        >
          {loading ? (
            <>
              <span className="w-3 h-3 border border-white/40 border-t-white rounded-full spin" />
              Auditing…
            </>
          ) : (
            <>
              <ScanText size={14} /> Audit page
            </>
          )}
        </button>
      </form>

      {error && (
        <div
          className="mt-4 flex items-center gap-2 border border-rose-200 bg-rose-50 text-rose-700 p-3 text-sm rounded-[4px]"
          data-testid="optimizer-error"
        >
          <AlertTriangle size={14} /> {error}
        </div>
      )}

      {report && (
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-4">
          <section className="card-flat p-5 lg:col-span-4" data-testid="optimizer-summary">
            <div className="label-eyebrow">Overall AEO score</div>
            <div className="mt-2 display text-6xl font-black tabular-nums">
              {report.overall_score}
            </div>
            <div className="mt-2 text-sm text-zinc-500">out of 100</div>
            <div className="mt-5 text-sm text-zinc-700">{report.summary}</div>
            <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
              <MiniStat k="Findings" v={findings.length} />
              <MiniStat k="High severity" v={highCount} tone="rose" />
              <MiniStat k="Sections" v={report.chunks?.length || 0} />
              <MiniStat k="Fixed" v={completedCount} tone="emerald" />
            </div>
            <div className="mt-5 space-y-2 text-xs text-zinc-500">
              <SignalRow
                label="FAQ schema"
                ok={report.has_faq_schema}
                on="Detected on page"
                off="Not detected"
              />
              <SignalRow
                label="llms.txt"
                ok={report.has_llms_txt}
                on="Published on domain"
                off="Not found"
              />
              <SignalRow
                label="Fresh content"
                ok={!report.is_stale}
                on={report.published_at ? `Published ${report.published_at.slice(0, 10)}` : "OK"}
                off={`Stale since ${report.published_at || "unknown"}`}
              />
            </div>
            <div className="mt-4 text-xs mono text-zinc-500 break-all">{report.url}</div>
          </section>

          <section className="lg:col-span-8 space-y-3" data-testid="optimizer-findings">
            <div className="flex items-center justify-between">
              <div className="label-eyebrow">Findings</div>
              <span className="pill mono text-[10px]">
                {findings.length} to fix · {completedCount} completed
              </span>
            </div>
            {findings.map((f) => (
              <FindingRow
                key={f.finding_id}
                finding={f}
                generating={!!generating[f.finding_id]}
                onFix={() => generateFix(f)}
                onOpen={() => f.artefact && setSelectedArtefact(f.artefact)}
                onToggle={(v) => toggleCompleted(f, v)}
              />
            ))}
            {!findings.length && (
              <div className="card-flat p-8 text-center text-sm text-zinc-500">
                No findings. This page is already well-structured for AI engines 🎯
              </div>
            )}
          </section>
        </div>
      )}

      <FixModal
        artefact={selectedArtefact}
        onClose={() => setSelectedArtefact(null)}
      />
    </DashboardShell>
  );
}

function FindingRow({ finding, generating, onFix, onOpen, onToggle }) {
  const meta = TYPE_META[finding.type] || { icon: Wand2, label: finding.type };
  const Icon = meta.icon;
  const hasArtefact = !!finding.artefact;
  const completed = finding.action?.status === "completed";
  return (
    <div className="card-flat p-5" data-testid={`finding-${finding.finding_id}`}>
      <div className="flex items-start gap-4">
        <div className="w-9 h-9 rounded-[4px] border border-zinc-200 flex items-center justify-center shrink-0">
          <Icon size={15} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-2 flex-wrap">
            <span
              className={`mono text-[10px] uppercase tracking-[0.14em] px-2 py-0.5 rounded-full border ${SEV_CLS[finding.severity] || SEV_CLS.medium}`}
            >
              {finding.severity}
            </span>
            <span className="mono text-[10px] uppercase tracking-[0.14em] text-zinc-500">
              {meta.label}
            </span>
            {finding.chunk_heading && (
              <span className="mono text-[10px] text-zinc-500 truncate max-w-[360px]">
                · {finding.chunk_heading}
              </span>
            )}
          </div>
          <h4 className="display mt-1 text-base font-bold tracking-tight">
            {finding.title}
          </h4>
          <p className="mt-1 text-sm text-zinc-600">{finding.description}</p>
        </div>
      </div>
      <div className="mt-4 flex items-center justify-between flex-wrap gap-3 border-t border-zinc-200 pt-3">
        <label
          className="inline-flex items-center gap-2 text-xs text-zinc-600 cursor-pointer"
          data-testid={`finding-toggle-${finding.finding_id}`}
        >
          <input
            type="checkbox"
            checked={completed}
            onChange={(e) => onToggle(e.target.checked)}
            disabled={!hasArtefact}
            className="accent-black w-3.5 h-3.5"
          />
          {completed ? (
            <span className="inline-flex items-center gap-1 text-emerald-700">
              <Check size={12} /> Completed — re-audit to update score
            </span>
          ) : hasArtefact ? (
            "Mark completed"
          ) : (
            "Generate a fix to mark complete"
          )}
        </label>
        <div className="flex items-center gap-2">
          {hasArtefact && (
            <button
              onClick={onOpen}
              data-testid={`finding-open-${finding.finding_id}`}
              className="text-xs border border-zinc-900 px-3 py-1.5 rounded-[4px] hover:bg-zinc-100 transition-colors"
            >
              Open artefact
            </button>
          )}
          <button
            onClick={onFix}
            disabled={generating}
            data-testid={`finding-fix-${finding.finding_id}`}
            className="inline-flex items-center gap-1.5 bg-black text-white text-xs px-3 py-1.5 rounded-[4px] hover:bg-zinc-800 transition-colors disabled:opacity-50"
          >
            {generating ? (
              <>
                <span className="w-3 h-3 border border-white/40 border-t-white rounded-full spin" />
                Generating…
              </>
            ) : hasArtefact ? (
              <>
                <Sparkles size={12} /> Regenerate
              </>
            ) : (
              <>
                <Wand2 size={12} /> Fix this
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function MiniStat({ k, v, tone }) {
  const toneCls =
    tone === "rose"
      ? "text-rose-700"
      : tone === "emerald"
      ? "text-emerald-700"
      : "text-zinc-900";
  return (
    <div className="border border-zinc-200 rounded-[4px] p-3">
      <div className="label-eyebrow">{k}</div>
      <div className={`display text-xl font-black mt-1 ${toneCls}`}>{v}</div>
    </div>
  );
}

function SignalRow({ label, ok, on, off }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="label-eyebrow">{label}</span>
      <span
        className={`text-[11px] ${ok ? "text-emerald-700" : "text-rose-700"}`}
      >
        {ok ? "✓" : "✗"} {ok ? on : off}
      </span>
    </div>
  );
}
